import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Form from '@nineeng/forms';
import { debounce } from 'lodash-es';
import { AccountSetupForm } from '@repo/forms';
import { determineUserProps } from '@repo/shared/helpers';
import { analytics, createFieldChangeHandler, createSectionNavigateHandler, identifyActor, taskUserProps } from '@repo/shared/analytics';
import { ConditionalTaskWrapper } from '@repo/components';
import { EventEmitter } from '@repo/shared/helpers';
import { Settings, LanguageCode } from '@repo/shared/domain';
import request from '@nineeng/request';
import { Task } from '@repo/shared/domain';
import { getTask, saveTaskDraft, updateTask } from 'src/api/access/tasks';
import { getMultipleSettings } from 'src/api/access/settings';
import { getMainSite } from 'src/api/access/sites';
import { getUserLanguage } from 'src/api/access/overview';
import AccessLayoutWrapper from 'src/layouts/access';
import LoadingDots from 'src/components/access/LoadingDots';
import { useAccessThemeWithV2Neutral } from 'src/hooks/useAccessThemeWithV2Neutral';
import useSessionStorage from 'src/hooks/useSessionStorage';
import { TaskCompletionScreen } from 'src/components/access/TaskCompletionScreen';

function formHasTestSection(formSchema: unknown): boolean {
	if (!formSchema || typeof formSchema !== 'object') return false;
	const schema = formSchema as { sections?: { type?: string }[]; type?: string };
	if (schema.sections?.some((section) => section.type === 'TestSection')) return true;
	return schema.type === 'TestSection';
}

export const Route = createFileRoute('/access_/tasks/$taskId')({
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { taskId } = Route.useParams();
	const search = useSearch({ strict: false }) as { lng?: string };
	const queryLanguage = search.lng || '';
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { value: authToken } = useSessionStorage('accessAuthToken');
	const [isClient, setIsClient] = useState(false);
	const [isUploading, setUploading] = useState(false);
	const [taskCompleted, setTaskCompleted] = useState(false);
	const availableLanguages: string[] = [];

	const { data: userLanguage } = useQuery({
		queryKey: ['userLanguage'],
		queryFn: getUserLanguage,
	});

	const { data: settings } = useQuery({
		queryKey: [`${[Settings.CustomerTheme, Settings.Languages].join(',')}-settings`],
		queryFn: () => getMultipleSettings([Settings.CustomerTheme, Settings.Languages]),
	});

	const themeSettings = settings?.[Settings.CustomerTheme];
	const languagesSettings = settings?.[Settings.Languages];

	const { effectiveThemeSettings } = useAccessThemeWithV2Neutral(themeSettings?.settings);

	const { data: fullTask } = useQuery({
		queryKey: ['task-detail', taskId, queryLanguage],
		queryFn: () => getTask(taskId, queryLanguage),
	});

	const revalidateTask = () => {
		queryClient.invalidateQueries({ queryKey: ['task-detail', taskId, queryLanguage] });
	};

	const revalidateOverview = () => {
		queryClient.invalidateQueries({ queryKey: ['userOverview'] });
	};

	const { data: site } = useQuery({
		queryKey: ['mainSite'],
		queryFn: getMainSite,
	});

	const taskAnalyticsProps = useMemo(() => ({
		task_id: taskId,
		task_name: fullTask?.name,
		task_type: fullTask?.type ?? (fullTask as any)?.template?.type,
		template_slug: (fullTask as any)?.templateSlug,
		source: 'access_portal' as const,
		...taskUserProps(fullTask?.user),
	}), [taskId, fullTask?.name, fullTask?.type, fullTask?.user?.id]);

	useEffect(() => {
		identifyActor(fullTask?.user);
	}, [fullTask?.user?.id]);

	useEffect(() => {
		analytics.capture('task_opened', { ...taskAnalyticsProps });
		const handler = () => { analytics.capture('task_abandoned', taskAnalyticsProps); };
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [taskId]);

	useEffect(() => {
		window.parent.postMessage({ isReady: true }, '*');
		setIsClient(true);
		const messageListener = ({ data }: MessageEvent) => {
			if (data.urlToOpen) {
				window.open(data.urlToOpen, '_blank');
			}
			if (data.close) {
				revalidateTask();
				revalidateOverview();
				navigate({ to: '/access/overview', search: { lng: queryLanguage } });
			}
		};
		window.addEventListener('message', messageListener);

		return () => {
			window.removeEventListener('message', messageListener);
		};
	}, [queryLanguage, navigate]);

	const userProps = useMemo(() => {
		if (fullTask) {
			const { user } = fullTask;
			return determineUserProps(fullTask, user);
		}
		return undefined;
	}, [fullTask]);

	const taskDataForDraft = useMemo(() => {
		if (!fullTask) return null;
		const { user, group, event, ...rest } = fullTask;
		return rest;
	}, [fullTask]);

	const shouldCommitForm = useMemo(
		() => !!taskDataForDraft?.Form?.saveDrafts || formHasTestSection(taskDataForDraft?.Form?.formSchema),
		[taskDataForDraft?.Form?.saveDrafts, taskDataForDraft?.Form?.formSchema],
	);

	const { handleFormChange, handleFormCommit } = useMemo(() => {
		if (!shouldCommitForm || !taskDataForDraft) {
			return { handleFormChange: () => {}, handleFormCommit: () => {} };
		}

		const saveDraft = async (data: Record<string, unknown>) => {
			const t = { ...taskDataForDraft, result: data };
			try {
				await saveTaskDraft(taskId, t, queryLanguage || undefined);
				analytics.capture('task_draft_saved', taskAnalyticsProps);
			} catch (e) {
				console.error(e);
			}
		};

		const debouncedSave = debounce(saveDraft, 1000);
		const commitDraft = (data: Record<string, unknown>) => {
			debouncedSave.cancel();
			return saveDraft(data);
		};

		return {
			handleFormChange: taskDataForDraft?.Form?.saveDrafts ? debouncedSave : () => {},
			handleFormCommit: commitDraft,
		};
	}, [shouldCommitForm, taskDataForDraft, taskId, queryLanguage, taskAnalyticsProps]);

	if (!userLanguage || !themeSettings || !languagesSettings || !site) {
		return <LoadingDots />;
	}

	const themeConfig = themeSettings.settings;
	const layoutTheme = effectiveThemeSettings ?? themeConfig;

	if (!fullTask) {
		return (
			<AccessLayoutWrapper
				themeSettings={layoutTheme}
				site={site}
				userLanguage={queryLanguage}
				languagesSettings={languagesSettings.settings}
			>
				<LoadingDots />
			</AccessLayoutWrapper>
		);
	}

	const { user, group, event, ...task } = fullTask as Task;

	// Identify dispatcher registration tasks (same pattern as used in backend)
	const isDispatcherTask =
		task.type === 'contractor-dispatcher-registration' || task.template?.type === 'contractor-dispatcher-registration';

	if (task?.Form?.availableLanguages) {
		availableLanguages.push(...task.Form.availableLanguages.map((l: string) => l.toLowerCase()));
	}

	if (task?.Form?.language) {
		const lang = task.Form.language.toLowerCase();
		if (!availableLanguages.includes(lang)) availableLanguages.push(lang);
	}

	// If one available language is found and queryLanguage was empty, set selectedLanguage to the one found language.
	let selectedLanguage = queryLanguage.toLowerCase();
	if (!selectedLanguage && availableLanguages.length == 1) {
		selectedLanguage = availableLanguages[0];
	}

	const shouldChooseLanguage =
		!!availableLanguages.length && !!selectedLanguage && !availableLanguages.includes(selectedLanguage);

	const handleSubmitDispatcher = async ({ _refData, ...data }: Record<string, unknown>) => {
		setUploading(true);

		const t = Object.assign({}, task) as Record<string, unknown>;

		t.status = Object.values(data).find((d: any) => d?.isFailed) ? 'failed' : 'done';

		t.result = data;
		try {
			// Use /api/access/:taskId endpoint for dispatcher tasks
			await request.put(`/api/access/${taskId}${queryLanguage ? `?lng=${queryLanguage}` : ''}`, t, {
				headers: {
					Authorization: `Basic ${authToken}`,
				},
			});
			analytics.capture('task_submitted', taskAnalyticsProps);
			setUploading(false);
			setTaskCompleted(true);
			// Revalidate to get updated task status - this will trigger a re-render showing the completion screen
			revalidateTask();
			queryClient.invalidateQueries({ queryKey: ['userOverview'] });
		} catch (e: unknown) {
			console.error(e);

			const error = e as { body?: { message?: string; details?: string[] }; message?: string; status?: number };
			const errorMessage =
				error?.body?.message ||
				error?.body?.details?.join(', ') ||
				error?.message ||
				'Error submitting task. Please check your input and try again.';

			EventEmitter.emit('toast', {
				type: 'error',
				message: errorMessage,
			});
			setUploading(false);
			// Don't redirect on error - keep the form visible so user can fix it
		}
	};

	const handleSubmit = async ({ _refData, ...data }: Record<string, unknown>) => {
		// If dispatcher task, use the dispatcher-specific handler
		if (isDispatcherTask) {
			return handleSubmitDispatcher({ _refData, ...data });
		}

		setUploading(true);

		const t = Object.assign({}, task) as Record<string, unknown>;

		if (task.template?.type === 'playit') {
			revalidateOverview();
			navigate({ to: '/access/overview' });
			return;
		}
		if (task.type === 'approval') {
			t.status = (data.approval as any)?.status === 'approved' ? 'done' : 'failed';
		} else {
			t.status = Object.values(data).find((d: any) => d?.isFailed) ? 'failed' : 'done';
		}

		t.result = data;
		try {
			await updateTask(taskId, t);
			analytics.capture('task_submitted', taskAnalyticsProps);
			setUploading(false);
			revalidateOverview();
			revalidateTask();
			queryClient.invalidateQueries({ queryKey: ['userOverview'] });
			navigate({ to: '/access/overview' });
		} catch (e) {
			analytics.capture('task_submit_failed', taskAnalyticsProps);
			console.error(e);
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error submitting task.',
			});
			setUploading(false);
		}
	};

	const handleClose = () => {
		// Close the page/window
		if ((window as any).ReactNativeWebView) {
			(window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'CLOSE_WEBVIEW' }));
		} else if (window.parent && window.parent !== window) {
			window.parent.postMessage({ close: true, task: true }, '*');
		} else {
			window.close();
		}
	};

	const changeLanguage = (lang: LanguageCode) => {
		if (queryLanguage !== lang) {
			navigate({ search: { lng: lang } });
			revalidateTask();
			return;
		}
	};

	if (!task || !task.Form) {
		return <div>Task or task form not found!</div>;
	}

	if (!isClient) {
		// disable SSR
		return null;
	}

	// For dispatcher tasks: if task is already done or just completed, show completion message and close button
	// For regular tasks: redirect happens in handleSubmit, so we don't need this check
	if (isDispatcherTask && (task.status === 'done' || taskCompleted)) {
		return (
			<AccessLayoutWrapper
				themeSettings={layoutTheme}
				site={site}
				userLanguage={queryLanguage}
				languagesSettings={languagesSettings.settings}
			>
				<TaskCompletionScreen isNewlyCompleted={taskCompleted} onClose={handleClose} />
			</AccessLayoutWrapper>
		);
	}

	return (
		<AccessLayoutWrapper
			themeSettings={layoutTheme}
			site={site}
			userLanguage={queryLanguage}
			languagesSettings={languagesSettings.settings}
		>
			<ConditionalTaskWrapper
				condition={!task.executePermission.isAllowed}
				showAlert={false}
				text={task.executePermission.denyReason}
			>
				<div className="flex flex-col justify-center items-center h-[100dvh] nine-task-overrides">
					<div className="w-full grow relative">
						{task.type === 'account' ? (
							<AccountSetupForm
								authorization={`Basic ${authToken}`}
								onSubmit={handleSubmit}
								userProps={userProps}
								customNavigationColors={{
									text: layoutTheme?.colors?.text,
									next: layoutTheme?.colors?.accent,
								}}
							/>
						) : (
							<Form
								originalValues={{
									...task.result,
									taskId: task.id,
									userProps: userProps || {},
								}}
								refData={{ user, group, event }}
								schema={task?.Form?.formSchema}
								onChange={handleFormChange}
								commit={handleFormCommit}
								onSubmit={handleSubmit}
								selectedLanguage={queryLanguage}
								disabled={!task.executePermission.isAllowed || ['done', 'expiryWarning'].includes(task.status)}
								customNavigationColors={{
									text: layoutTheme?.colors?.text,
									next: layoutTheme?.colors?.accent,
								}}
								onFieldChange={createFieldChangeHandler(taskAnalyticsProps)}
								onSectionNavigate={createSectionNavigateHandler(taskAnalyticsProps)}
							/>
						)}
					</div>
				</div>
			</ConditionalTaskWrapper>

			<Backdrop
				sx={{
					color: '#fff',
					zIndex: (theme) => theme.zIndex.drawer + 1,
					display: 'flex',
					flexDirection: 'column',
					padding: 2,
				}}
				open={shouldChooseLanguage}
			>
				This task is not fully available in your preferred language, please select one that is available to continue:
				<div className="flex flex-wrap gap-2 mt-2 justify-evenly">
					{availableLanguages.map((availableLanguage: string) => (
						<button
							key={availableLanguage}
							className="w-[50px] h-[50px] bg-white border border-primary text-primary rounded hover:bg-primary hover:text-white transition-colors duration-300"
							onClick={() => {
								changeLanguage(availableLanguage as LanguageCode);
							}}
						>
							{availableLanguage}
						</button>
					))}
				</div>
			</Backdrop>

			<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUploading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</AccessLayoutWrapper>
	);
}
